import React, { useState } from 'react';
import styled from 'styled-components';
import { ChevronDown, ChevronUp, AlertCircle, Check, Tag } from 'lucide-react';
import { PaymentDto, PaymentStatus, formatCurrency, formatDate } from '../../services/paymentsService';
import { DiscountDto } from '../../services/discountsService';

interface PaymentHistoryProps {
  payments: PaymentDto[];
  discounts?: DiscountDto[]; // הנחות מוצגות כשורות זיכוי
}

// פריט מאוחד להיסטוריה: תשלום או הנחה (זיכוי)
type HistoryItem =
  | { kind: 'payment'; date: string; data: PaymentDto }
  | { kind: 'discount'; date: string; data: DiscountDto };

const Container = styled.div`
  margin-top: 24px;
`;

const Header = styled.div<{ $isExpanded: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: var(--background-secondary);
  border-radius: ${props => props.$isExpanded ? 'var(--border-radius-md) var(--border-radius-md) 0 0' : 'var(--border-radius-md)'};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--hover-background);
  }

  @media (max-width: 767px) {
    padding: 12px 16px;
  }
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
`;

const Count = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: var(--text-secondary);
`;

const ChevronWrapper = styled.div`
  color: var(--text-secondary);
  transition: transform 0.2s ease;
`;

const Content = styled.div<{ $isExpanded: boolean }>`
  max-height: ${props => props.$isExpanded ? '500px' : '0'};
  overflow: ${props => props.$isExpanded ? 'auto' : 'hidden'};
  transition: max-height 0.3s ease;
  background: var(--surface-color);
  border: ${props => props.$isExpanded ? '1px solid var(--border-color)' : 'none'};
  border-top: none;
  border-radius: 0 0 var(--border-radius-md) var(--border-radius-md);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  @media (max-width: 767px) {
    font-size: 12px;
  }
`;

const Th = styled.th`
  text-align: center;
  padding: 12px 16px;
  background: var(--background-secondary);
  font-weight: 600;
  color: var(--text-secondary);
  font-size: 12px;
  border-bottom: 1px solid var(--border-color);

  @media (max-width: 767px) {
    padding: 8px 12px;
  }
`;

const Td = styled.td`
  text-align: center;
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-color);
  color: var(--text-primary);

  @media (max-width: 767px) {
    padding: 8px 12px;
  }
`;

const Tr = styled.tr`
  &:last-child td {
    border-bottom: none;
  }

  &:hover {
    background: var(--hover-background);
  }
`;

const AmountCell = styled.td<{ $failed?: boolean }>`
  text-align: center;
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-color);
  color: ${props => props.$failed ? '#DC2626' : '#16a34a'};
  font-weight: 600;
  direction: ltr;

  @media (max-width: 767px) {
    padding: 8px 12px;
  }
`;

const StatusBadge = styled.span<{ $variant: 'success' | 'failed' | 'pending' | 'discount' }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: var(--border-radius-sm);
  font-size: 11px;
  font-weight: 600;
  background: ${props =>
    props.$variant === 'success' ? 'rgba(22, 163, 74, 0.1)' :
    props.$variant === 'failed' ? 'rgba(220, 38, 38, 0.1)' :
    props.$variant === 'discount' ? 'rgba(147, 51, 234, 0.1)' :
    'rgba(217, 119, 6, 0.1)'};
  color: ${props =>
    props.$variant === 'success' ? '#16a34a' :
    props.$variant === 'failed' ? '#DC2626' :
    props.$variant === 'discount' ? '#9333ea' :
    '#d97706'};
  cursor: ${props => props.$variant === 'failed' ? 'help' : 'default'};
`;

const DiscountTr = styled.tr`
  background: rgba(147, 51, 234, 0.04);

  &:last-child td {
    border-bottom: none;
  }

  &:hover {
    background: rgba(147, 51, 234, 0.08);
  }
`;

const DiscountAmountCell = styled.td`
  text-align: center;
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-color);
  color: #9333ea;
  font-weight: 600;
  direction: ltr;

  @media (max-width: 767px) {
    padding: 8px 12px;
  }
`;

const FailedTr = styled.tr`
  background: rgba(220, 38, 38, 0.04);

  &:last-child td {
    border-bottom: none;
  }

  &:hover {
    background: rgba(220, 38, 38, 0.08);
  }
`;

const EmptyState = styled.div`
  padding: 32px;
  text-align: center;
  color: var(--text-secondary);
  font-size: 14px;
`;

const PaymentHistory: React.FC<PaymentHistoryProps> = ({ payments, discounts }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // מיזוג תשלומים והנחות (זיכויים) למיון אחיד לפי תאריך יורד
  const items: HistoryItem[] = [
    ...(payments || []).map(p => ({ kind: 'payment' as const, date: p.paymentDate, data: p })),
    ...(discounts || []).map(d => ({ kind: 'discount' as const, date: d.discountDate, data: d })),
  ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const itemCount = items.length;

  return (
    <Container>
      <Header
        $isExpanded={isExpanded}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <HeaderTitle>
          היסטוריית תשלומים
          <Count>({itemCount})</Count>
        </HeaderTitle>
        <ChevronWrapper>
          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </ChevronWrapper>
      </Header>

      <Content $isExpanded={isExpanded}>
        {itemCount > 0 ? (
          <Table>
            <thead>
              <tr>
                <Th>תאריך</Th>
                <Th>אמצעי תשלום</Th>
                <Th>אסמכתא</Th>
                <Th>סכום</Th>
                <Th>סטטוס</Th>
                <Th>נרשם ע"י</Th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => {
                if (item.kind === 'discount') {
                  const d = item.data;
                  return (
                    <DiscountTr key={`d-${d.id}`}>
                      <Td>{formatDate(d.discountDate)}</Td>
                      <Td>הנחה ({d.typeName})</Td>
                      <Td>-</Td>
                      <DiscountAmountCell>−{formatCurrency(d.effectiveAmount)}</DiscountAmountCell>
                      <Td>
                        <StatusBadge $variant="discount">
                          <Tag size={12} />
                          זיכוי
                        </StatusBadge>
                      </Td>
                      <Td>-</Td>
                    </DiscountTr>
                  );
                }

                const payment = item.data;
                const isFailed = payment.status === PaymentStatus.Failed;
                const isPending = payment.status === PaymentStatus.Pending;
                const RowComponent = isFailed ? FailedTr : Tr;
                return (
                  <RowComponent key={`p-${payment.id}`}>
                    <Td>{formatDate(payment.paymentDate)}</Td>
                    <Td>{payment.paymentMethodName}</Td>
                    <Td>{payment.referenceNumber || '-'}</Td>
                    <AmountCell $failed={isFailed}>{formatCurrency(payment.amount)}</AmountCell>
                    <Td>
                      {isFailed ? (
                        <StatusBadge $variant="failed" title={payment.failureReason || 'נכשל'}>
                          <AlertCircle size={12} />
                          נכשל
                        </StatusBadge>
                      ) : isPending ? (
                        <StatusBadge $variant="pending">ממתין</StatusBadge>
                      ) : (
                        <StatusBadge $variant="success">
                          <Check size={12} />
                          שולם
                        </StatusBadge>
                      )}
                    </Td>
                    <Td>{payment.recordedByName}</Td>
                  </RowComponent>
                );
              })}
            </tbody>
          </Table>
        ) : (
          <EmptyState>טרם נרשמו תשלומים</EmptyState>
        )}
      </Content>
    </Container>
  );
};

export default PaymentHistory;
